import React from "react";
import { CSSTransition } from "react-transition-group";
import {
  EngagementChosenAnswer,
  TrainingCenterBundleItemVideoEngagementQuestion,
} from "../../system/AppState/AppStateTrainingCenter";
import { IsDefined } from "../Generic/IsDefined";
import { TrainingCenterLanguagePreference } from "./TrainingCenterLanguageLookup";
import TrainingCenterVideoEngagementQuestion from "./TrainingCenterVideoEngagementQuestion";
import TrainingCenterVideoEngagementQuestionResult from "./TrainingCenterVideoEngagementQuestionResult";

const ANSWER_DISPLAY_DURATION = 2; // seconds

function isEngagementQuestionInRange(
  currentSecond: number,
  { atSecond, duration }: TrainingCenterBundleItemVideoEngagementQuestion
) {
  const hasQuestionStarted = atSecond <= currentSecond;
  const hasQuestionEnded = atSecond + duration <= currentSecond;

  return hasQuestionStarted && !hasQuestionEnded;
}

function isEngagementQuestionAnswerInRange(
  currentSecond: number,
  answer: EngagementChosenAnswer | undefined
) {
  if (answer === undefined) {
    return false;
  }

  const hasAnswerStarted = answer.atSecond <= currentSecond;
  const hasAnswerEnded = answer.atSecond + ANSWER_DISPLAY_DURATION <= currentSecond;

  return hasAnswerStarted && !hasAnswerEnded;
}

interface Props {
  currentSecond: number;
  engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion;
  chosenEngagementAnswer: EngagementChosenAnswer | undefined;
  isQuestionClosed: boolean;
  languagePreference: TrainingCenterLanguagePreference;
  onClickAnswer: (
    engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion,
    answerValue: string,
    currentSecond: number
  ) => void;
  onClickClose: (engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion) => void;
  onBeforeTimeoutEnd: () => void;
}

const TrainingCenterVideoEngagementQuestionWrapper = ({
  currentSecond,
  engagementQuestion,
  chosenEngagementAnswer,
  isQuestionClosed,
  languagePreference,
  onClickAnswer,
  onClickClose,
  onBeforeTimeoutEnd,
}: Props) => {
  const timeLeft = React.useMemo(
    () => engagementQuestion.atSecond + engagementQuestion.duration - currentSecond,
    [engagementQuestion.atSecond, engagementQuestion.duration, currentSecond]
  );

  const isQuestionInRange = React.useMemo(
    () => isEngagementQuestionInRange(currentSecond, engagementQuestion),
    [currentSecond, engagementQuestion]
  );

  const isAnswerInRange = React.useMemo(
    () => isEngagementQuestionAnswerInRange(currentSecond, chosenEngagementAnswer),
    [currentSecond, chosenEngagementAnswer]
  );

  return (
    <>
      <IsDefined value={chosenEngagementAnswer}>
        {(chosenAnswer) => (
          <CSSTransition
            in={isAnswerInRange}
            timeout={250}
            classNames="TrainingCenterVideoEngagementQuestionResult"
            unmountOnExit={true}
          >
            <TrainingCenterVideoEngagementQuestionResult
              question={engagementQuestion}
              chosenAnswer={chosenAnswer}
              languagePreference={languagePreference}
            />
          </CSSTransition>
        )}
      </IsDefined>

      <CSSTransition
        in={isQuestionInRange && !isQuestionClosed && chosenEngagementAnswer === undefined}
        timeout={250}
        classNames="TrainingCenterVideoEngagementQuestion"
        unmountOnExit={true}
      >
        <TrainingCenterVideoEngagementQuestion
          currentSecond={currentSecond}
          engagementQuestion={engagementQuestion}
          chosenAnswer={chosenEngagementAnswer}
          timeLeft={timeLeft}
          languagePreference={languagePreference}
          onClickAnswer={onClickAnswer}
          onClickClose={onClickClose}
          onBeforeTimeoutEnd={onBeforeTimeoutEnd}
        />
      </CSSTransition>
    </>
  );
};

export default React.memo(TrainingCenterVideoEngagementQuestionWrapper);
